import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FileWithPath } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import { getThreadDocumentContext } from '@/utils/documentContext';

import {
  FileSpec,
  IStep,
  IThread,
  useAuth,
  useChatData,
  useChatInteract,
  chatInputValueState
} from '@chainlit/react-client';

import { Globe, FileText } from 'lucide-react';

import { Settings } from '@/components/icons/Settings';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useUISettings } from '@/hooks/useUISettings';

import { chatSettingsOpenState } from '@/state/project';
import {
  IAttachment,
  attachmentsState,
  persistentCommandState,
  webSearchEnabledState,
  documentContextEnabledState,
  uploadedDocumentNamesState
} from 'state/chat';

import { Attachments } from './Attachments';
import CommandButtons from './CommandButtons';
import CommandButton from './CommandPopoverButton';
import Input, { InputMethods } from './Input';
import McpButton from './Mcp';
import SubmitButton from './SubmitButton';
import UploadButton from './UploadButton';
import VoiceButton from './VoiceButton';
import AssistantVoiceButton from './AssistantVoiceButton';

interface Props {
  fileSpec: FileSpec;
  onFileUpload: (payload: FileWithPath[]) => void;
  onFileUploadError: (error: string) => void;
  autoScrollRef: MutableRefObject<boolean>;
  thread?: IThread;
}

export default function MessageComposer({
  fileSpec,
  onFileUpload,
  onFileUploadError,
  autoScrollRef,
  thread
}: Props) {
  const inputRef = useRef<InputMethods>(null);
  const [value, setValue] = useState('');
  const [chatInputValue, setChatInputValue] = useRecoilState(chatInputValueState);
  const [selectedCommand, setSelectedCommand] = useRecoilState(
    persistentCommandState
  );
  const setChatSettingsOpen = useSetRecoilState(chatSettingsOpenState);
  const [attachments, setAttachments] = useRecoilState(attachmentsState);
  const [webSearchEnabled, setWebSearchEnabled] = useRecoilState(
    webSearchEnabledState
  );
  // Document context can come from two sources:
  // 1. Session state: newly uploaded documents in current session
  // 2. Thread metadata: documents uploaded in previous sessions (persistent)
  const sessionDocumentContextEnabled = useRecoilState(documentContextEnabledState)[0];
  const sessionUploadedDocumentNames = useRecoilState(uploadedDocumentNamesState)[0];
  
  // Extract document context from thread using utility function
  const threadDocumentContext = useMemo(() => getThreadDocumentContext(thread), [thread]);
  
  // Use thread data if available, otherwise fall back to session state
  const documentContextEnabled = threadDocumentContext.enabled || sessionDocumentContextEnabled;
  const uploadedDocumentNames = threadDocumentContext.documentNames.length > 0 
    ? threadDocumentContext.documentNames 
    : sessionUploadedDocumentNames;
  
  const { t } = useTranslation();

  const { user } = useAuth();
  const { sendMessage, replyMessage } = useChatInteract();
  const { askUser, chatSettingsInputs, disabled: _disabled } = useChatData();
  
  // Get UI settings to check which voice button to render
  const { settings: uiSettings } = useUISettings();
  
  // Debug logging
  useEffect(() => {
    console.log('[Assistant] MessageComposer - UI Settings loaded:', uiSettings);
    console.log('[Assistant] MessageComposer - assistantMicEnabled:', uiSettings?.assistantMicEnabled);
    console.log('[Assistant] MessageComposer - voiceMode:', uiSettings?.voiceMode);
  }, [uiSettings]);

  const disabled = _disabled || !!attachments.find((a) => !a.uploaded);
  
  console.log('[Assistant] MessageComposer - Rendering with uiSettings:', {
    assistantMicEnabled: uiSettings?.assistantMicEnabled,
    voiceMode: uiSettings?.voiceMode,
    disabled
  });

  // Sync chatInputValue from Recoil (set by socket event) to local state
  useEffect(() => {
    if (chatInputValue && chatInputValue !== value) {
      setValue(chatInputValue);
      // Clear the Recoil state after syncing to prevent re-triggering
      setChatInputValue('');
      // Focus the input
      inputRef.current?.focus();
    }
  }, [chatInputValue, setChatInputValue]);

  useEffect(() => {
    // Empty dependency, we don't need logging here
  }, [webSearchEnabled, disabled]);

  const onPaste = useCallback(
    (event: ClipboardEvent) => {
      if (event.clipboardData && event.clipboardData.items) {
        const items = Array.from(event.clipboardData.items);

        items.forEach((item) => {
          if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file) {
              onFileUpload([file as FileWithPath]);
            }
          }
        });
      }
    },
    [onFileUpload]
  );
  const onSubmit = useCallback(
    async (
      msg: string,
      attachments?: IAttachment[],
      selectedCommand?: string
    ) => {
      const message: IStep = {
        threadId: '',
        command: selectedCommand,
        id: uuidv4(),
        name: user?.identifier || 'User',
        type: 'user_message',
        output: msg,
        createdAt: new Date().toISOString(),
        metadata: {
          location: window.location.href,
          webSearchEnabled,
          webSearch: {
            enabled: webSearchEnabled,
            name: 'websearch'
          }
        }
      };

      const fileReferences = attachments
        ?.filter((a) => !!a.serverId)
        .map((a) => ({ id: a.serverId! }));

      if (autoScrollRef) {
        autoScrollRef.current = true;
      }
      sendMessage(message, fileReferences);
    },
    [user, sendMessage, webSearchEnabled]
  );

  const onReply = useCallback(
    async (msg: string) => {
      const message: IStep = {
        threadId: '',
        id: uuidv4(),
        name: user?.identifier || 'User',
        type: 'user_message',
        output: msg,
        createdAt: new Date().toISOString(),
        metadata: {
          location: window.location.href,
          webSearchEnabled,
          webSearch: {
            enabled: webSearchEnabled,
            name: 'websearch'
          }
        }
      };

      replyMessage(message);
      if (autoScrollRef) {
        autoScrollRef.current = true;
      }
    },
    [user, replyMessage, webSearchEnabled]
  );

  const submit = useCallback(() => {
    if (disabled || (value === '' && attachments.length === 0)) {
      return;
    }
    if (askUser) {
      onReply(value);
    } else {
      onSubmit(value, attachments, selectedCommand?.id);
    }
    setAttachments([]);
    inputRef.current?.reset();
  }, [
    value,
    disabled,
    setValue,
    askUser,
    attachments,
    selectedCommand,
    setAttachments,
    onSubmit
  ]);
  return (
    <div
      id="message-composer"
      className={cn(
        'rounded-3xl w-full min-h-24 flex flex-col transition-all duration-300',
        // Mobile responsive padding
        'p-2 px-3 sm:p-3 sm:px-4',
        // Better mobile touch targets
        'min-h-[56px] sm:min-h-24',
        // Dramatic background change for web mode
        webSearchEnabled
          ? 'bg-blue-50 dark:bg-blue-950/20 border-2 border-blue-200 dark:border-blue-800'
          : 'bg-accent dark:bg-card'
      )}
    >
      {/* Web Mode Warning Banner */}
      {webSearchEnabled && (
        <div className="mb-3 -mx-1 px-3 py-2 bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Globe className="size-4 text-blue-600 dark:text-blue-400" strokeWidth={2} />
              <span className="font-semibold text-blue-800 dark:text-blue-200 text-sm">
                WEB MODE ACTIVE
              </span>
            </div>
          </div>
          <p className="text-xs text-blue-700 dark:text-blue-300 mt-1 leading-relaxed">
            Your queries will search the internet. Be cautious about sharing sensitive or confidential information.
          </p>
        </div>
      )}

      {/* Document Context Banner */}
      {documentContextEnabled && uploadedDocumentNames.length > 0 && (
        <div className="mb-3 -mx-1 px-3 py-2 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <FileText className="size-4 text-green-600 dark:text-green-400" strokeWidth={2} />
              <span className="font-semibold text-green-800 dark:text-green-200 text-sm">
                DOCUMENT CONTEXT ACTIVE
              </span>
            </div>
          </div>
          <p className="text-xs text-green-700 dark:text-green-300 mt-1 leading-relaxed">
            Using {uploadedDocumentNames.length === 1 ? 'document' : 'documents'}: {uploadedDocumentNames.map((name: string, index: number) => (
              <span key={name} className="font-medium">
                {name}{index < uploadedDocumentNames.length - 1 ? ', ' : ''}
              </span>
            ))}
          </p>
          <p className="text-xs text-green-700 dark:text-green-300 mt-2 leading-relaxed border-t border-green-200 dark:border-green-800 pt-2">
            <strong>Note:</strong> The context of your chatbot conversations are tied to your uploaded document(s). Max limit of 2 documents per chat window. [CSV/Excel uploads are supported as well; max limit of 1 file]
          </p>
        </div>
      )}
      
      {attachments.length > 0 ? (
        <div className="mb-1">
          <Attachments />
        </div>
      ) : null}
      <Input
        ref={inputRef}
        id="chat-input"
        autoFocus
        selectedCommand={selectedCommand}
        setSelectedCommand={setSelectedCommand}
        value={value}
        onChange={setValue}
        onPaste={onPaste}
        onEnter={submit}
        className="mb-2 sm:mb-3"
        placeholder={t('chat.input.placeholder')}
      />
      <div
        className={cn(
          'flex items-center justify-between',
          // Mobile: better spacing and ensure button visibility in WebView
          'gap-2 sm:gap-0',
          'min-h-[40px]' // Ensure minimum height for button visibility
        )}
      >
        <div
          className={cn(
            'flex items-center',
            // Mobile: better spacing and wrapping
            '-ml-1 sm:-ml-1.5',
            'flex-wrap sm:flex-nowrap',
            'gap-1 sm:gap-0',
            'flex-1 min-w-0' // Allow shrinking on mobile but maintain minimum width
          )}
        >
          <UploadButton
            disabled={disabled || webSearchEnabled}
            webSearchEnabled={webSearchEnabled}
            fileSpec={fileSpec}
            onFileUploadError={onFileUploadError}
            onFileUpload={onFileUpload}
          />
          <CommandButton
            disabled={disabled}
            onCommandSelect={setSelectedCommand}
          />
          {chatSettingsInputs.length > 0 && (
            <Button
              id="chat-settings-open-modal"
              disabled={disabled}
              onClick={() => setChatSettingsOpen(true)}
              className="hover:bg-muted"
              variant="ghost"
              size="icon"
            >
              <Settings className="!size-6" />
            </Button>
          )}
          <McpButton disabled={disabled} />
          {/* Standard voice button - always shown when voice is enabled */}
          <VoiceButton disabled={disabled} />
          {/* Assistant voice button moved to header - AIAvatarButton */}
          <Button
            id="web-search-toggle"
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              'transition-colors hover:bg-muted',
              documentContextEnabled 
                ? 'text-muted-foreground/50 opacity-50 cursor-not-allowed' 
                : webSearchEnabled
                ? 'text-blue-600 dark:text-blue-400 bg-blue-100/50 dark:bg-blue-900/30 hover:bg-blue-200/50 dark:hover:bg-blue-900/50'
                : 'text-muted-foreground'
            )}
            aria-pressed={webSearchEnabled}
            aria-label={
              documentContextEnabled 
                ? 'Web search is disabled in document context mode'
                : webSearchEnabled ? 'Disable web search' : 'Enable web search'
            }
            onClick={() => {
              if (!documentContextEnabled) {
                const nextState = !webSearchEnabled;
                setWebSearchEnabled(nextState);
              }
            }}
            disabled={disabled || documentContextEnabled}
            title={
              documentContextEnabled 
                ? 'Web search is disabled when using document context'
                : webSearchEnabled ? 'Disable web search' : 'Enable web search'
            }
          >
            <Globe className="!size-5" strokeWidth={webSearchEnabled ? 2.5 : 1.75} />
          </Button>
          <CommandButtons
            disabled={disabled}
            selectedCommandId={selectedCommand?.id}
            onCommandSelect={setSelectedCommand}
          />
        </div>
        <div
          className={cn(
            'flex items-center gap-1',
            'flex-shrink-0', // Never shrink the send button
            'ml-2' // Ensure spacing from left content
          )}
        >
          <SubmitButton
            onSubmit={submit}
            disabled={disabled || (value.trim() === '' && attachments.length === 0)}
          />
        </div>
      </div>
    </div>
  );
}
